/**
 * Customer Accounts Redux Selectors
 */

import { createSelector } from '@reduxjs/toolkit'
import type { CustomerAccount, CustomerAccountsState, CustomerAccountStats } from '../domain/types'

interface RootState {
  customerAccounts: CustomerAccountsState
}

// Base selectors
export const selectCustomerAccountsState = (state: RootState) => state.customerAccounts
export const selectAllAccounts = (state: RootState) => state.customerAccounts.items
export const selectSelectedAccount = (state: RootState) => state.customerAccounts.selectedAccount
export const selectAccountsStats = (state: RootState) => state.customerAccounts.stats
export const selectAccountsFilter = (state: RootState) => state.customerAccounts.filter
export const selectAccountsPagination = (state: RootState) => state.customerAccounts.pagination
export const selectAccountsLoading = (state: RootState) => state.customerAccounts.loading
export const selectAccountsError = (state: RootState) => state.customerAccounts.error

// Filtering is now server-side via API query params.
export const selectFilteredAccounts = createSelector(
  [selectAllAccounts],
  (accounts: CustomerAccount[]) => accounts
)

// Stats selectors
export const selectActiveAccountsCount = createSelector([selectAllAccounts], (accounts: CustomerAccount[]) =>
  accounts.filter((a: CustomerAccount) => a.accountStatus === 'active').length
)

export const selectSuspendedAccountsCount = createSelector([selectAllAccounts], (accounts: CustomerAccount[]) =>
  accounts.filter((a: CustomerAccount) => a.accountStatus === 'suspended').length
)

export const selectBlockedAccountsCount = createSelector([selectAllAccounts], (accounts: CustomerAccount[]) =>
  accounts.filter((a: CustomerAccount) => a.accountStatus === 'blocked').length
)

// Accounts by tenant
export const selectAccountsByTenant = createSelector([selectAllAccounts], (accounts: CustomerAccount[]) => {
  const byTenant: Record<string, CustomerAccount[]> = {}
  accounts.forEach((account: CustomerAccount) => {
    const tenantId = account.tenant.id
    if (!byTenant[tenantId]) {
      byTenant[tenantId] = []
    }
    byTenant[tenantId].push(account)
  })
  return byTenant
})

// Select account by ID
export const selectAccountById = (accountId: string) =>
  createSelector([selectAllAccounts], (accounts: CustomerAccount[]) => accounts.find((a: CustomerAccount) => a.id === accountId) ?? null)

// Computed stats from items
export const selectComputedStats = createSelector(
  [selectAccountsStats],
  (stats: CustomerAccountStats) => stats
)

// Accounts with overdue
export const selectAccountsWithOverdue = createSelector([selectAllAccounts], (accounts: CustomerAccount[]) =>
  accounts.filter((a: CustomerAccount) => (a.overdueAmount ?? 0) > 0)
)

export const selectOverdueCount = createSelector(
  [selectAccountsWithOverdue],
  (accounts) => accounts.length
)
